import IdGenerator from 'scripts/domain/contracts/id_generator';

import { InfrastructureError } from 'scripts/application/lib/error';
import { handleCommonErrors } from 'scripts/application/lib/gateway_error_silent_handler';
import {
  LOADING_SAFETY_TIMEOUT_MS,
  clearLoadingSafetyTimer,
  startLoadingSafetyTimer,
} from 'actions/team_assist/lib/loading_safety_timer';
import TeamAssistMessage from 'models/team_assist_message';

// Re-exported for tests that need to advance the safety timer.
export { LOADING_SAFETY_TIMEOUT_MS };

export default class AskTeamAssist {
  constructor(context) {
    this.context = context;
  }

  run({ customerId, question, displayedMessage }) {
    if (!customerId) {
      return;
    }
    if (!question) {
      return;
    }

    if (!this.context.stores.customers.has({ id: customerId })) {
      return;
    }

    const { teamAssists } = this.context.stores.customers.storesFor(customerId);

    this.context.analytics.track('Team Assist Question Asked', { customerId });

    const messageId = IdGenerator.newId();
    const optimisticMessage = TeamAssistMessage.create({
      content: question,
      createdAt: new Date().toISOString(),
      customerId,
      displayedMessage,
      id: messageId,
      role: TeamAssistMessage.Role.agent_question,
      type: TeamAssistMessage.Type.question,
    });
    teamAssists.addOrReplace(optimisticMessage);
    teamAssists.setLoading();
    startLoadingSafetyTimer(customerId, teamAssists);

    const body = { id: messageId, question };
    if (displayedMessage) {
      body.displayedMessage = displayedMessage;
    }

    // Attach the contact the *asking* agent currently has open so the backend
    // can attribute this ask to a specific contact and conversation. Read it
    // from the asking agent's own active sessions — never the conversation's
    // queue item — so an agent merely viewing a conversation they are not
    // handling (e.g. a supervisor reading along) is not credited against the
    // assigned agent's contact. When there is no active session (profile
    // browsing, viewing someone else's conversation) we send neither and the
    // backend falls back to its best-effort conversation lookup.
    const activeSession = this.context.stores.activeSessions.findBy({ customer: { id: customerId } });
    if (activeSession) {
      const { conversationId, sessions } = activeSession.queueItem;
      if (conversationId) {
        body.conversationId = conversationId;
      }
      // A queue item can hold multiple concurrent sessions (e.g. a phone call
      // with a parallel SMS). Only attribute to a specific contact when it is
      // unambiguous; otherwise the ask stays conversation-grained.
      if (sessions && sessions.length === 1) {
        body.communicationSessionId = sessions[0].id;
      }
    }

    // Supernova decides whether to handle this fire-and-forget (response
    // returns only the question; AI messages arrive via MQTT broadcast) or
    // synchronously (response also carries the AI messages inline). Both
    // shapes must be tolerated so this client survives any deploy ordering
    // — including the rollout window where supernova has been upgraded but
    // this bundle has not. We detect the inline shape by the presence of
    // any ai_response message in the response body.
    this.context.gateways.teamAssist
      .add({ customerId }, body)
      .then(response => {
        const aiMessages = (response && response.messages ? response.messages : []).filter(
          m => m && m.role === TeamAssistMessage.Role.ai_response
        );
        if (aiMessages.length === 0) {
          return;
        }
        aiMessages.forEach(m => teamAssists.addOrReplace(TeamAssistMessage.fromJs(m)));
        teamAssists.resetLoading();
        clearLoadingSafetyTimer(customerId);
      })
      .catch(err => {
        // Timeouts and other transport errors do NOT mean the agent failed —
        // the work may still complete and stream back. Only mark the message
        // as failed for definite rejections (validation, permission, etc.)
        // raised before the agent was queued.
        if (!(err instanceof InfrastructureError)) {
          const msg = teamAssists.find(messageId);
          if (msg) {
            teamAssists.addOrReplace(TeamAssistMessage.fromJs({ ...msg.toJs(), error: true }));
          }
          teamAssists.resetLoading();
          clearLoadingSafetyTimer(customerId);
        }
        handleCommonErrors(this.context, err);
      });
  }
}
